import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { User } from '../types/user.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { ADMIN_USERS_API_PATHS } from '../constants/users-paths.constants';
import { IResponseError } from '../../shared/interfaces/response.interface';

interface FindUserIdByIdState {
  loading: boolean;
  user: User | null;
}

export function useFindUserIdById(userId: string | undefined) {
  const { enqueueSnackbar } = useSnackbar();

  const [result, setResult] = useState<FindUserIdByIdState>({
    loading: Boolean(userId),
    user: null,
  });

  const fetchUser = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!userId) {
        setResult({ loading: false, user: null });
        return;
      }

      // A silent refetch (after an update) keeps the current user displayed instead of flashing the skeleton.
      setResult((previousResult) => ({
        loading: !silent,
        user: silent ? previousResult.user : null,
      }));

      try {
        const user = await HttpClient.get<User>(ADMIN_USERS_API_PATHS.GET_USER_BY_ID(userId));
        setResult({ loading: false, user });
      } catch (error) {
        const message =
          (error as IResponseError).error?.message ??
          "Une erreur est survenue lors de la récupération de l'utilisateur";
        enqueueSnackbar(message, { variant: 'error' });
        setResult({ loading: false, user: null });
      }
    },
    [userId] // enqueueSnackbar left out on purpose: notistack keeps it stable and listing it would refetch on every render under test mocks.
  );

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const refetchUser = useCallback(() => fetchUser({ silent: true }), [fetchUser]);

  return { ...result, refetchUser };
}
